/**
 * Component interaction tracking for Mixpanel (fhs-component-interacted).
 * Tracks meaningful non-CTA interactions: accordion, tabs, carousel, filter, calendar, gallery, menu.
 *
 * All interaction types must come from this controlled enum.
 * No hover-only or scroll-based events.
 */

/** Controlled enum of valid interaction types */
export const INTERACTION_TYPES = Object.freeze({
  ACCORDION_EXPANDED: 'accordion_expanded',
  ACCORDION_COLLAPSED: 'accordion_collapsed',
  TAB_CHANGED: 'tab_changed',
  CAROUSEL_NEXT: 'carousel_next',
  CAROUSEL_PREV: 'carousel_prev',
  FILTER_APPLIED: 'filter_applied',
  CALENDAR_OPENED: 'calendar_opened',
  DATE_SELECTED: 'date_selected',
  GALLERY_NEXT: 'gallery_next',
  GALLERY_PREV: 'gallery_prev',
  MENU_OPENED: 'menu_opened',
  MENU_CLOSED: 'menu_closed',
});

/** Set of valid interaction type values for validation */
const VALID_INTERACTION_TYPES = new Set(Object.values(INTERACTION_TYPES));

const EVENT_NAME = 'fhs-component-interacted';

/**
 * Find the nearest ancestor (or self) with FHS component data attributes.
 * @param {HTMLElement} el
 * @returns {HTMLElement|null}
 */
function findComponentElement(el) {
  if (!el || typeof el.closest !== 'function') return null;
  const componentEl = el.closest(
    '[data-fhs-component-type], [data-fhs-component-id]',
  );
  return componentEl || el;
}

/**
 * Get component context from element data attributes.
 * @param {HTMLElement} [el]
 * @returns {{ component_type?: string, component_id?: string, pattern_id?: string }}
 */
export function getComponentContext(el) {
  if (!el || typeof el.getAttribute !== 'function') return {};
  return {
    component_type: el.getAttribute('data-fhs-component-type') || undefined,
    component_id: el.getAttribute('data-fhs-component-id') || undefined,
    pattern_id: el.getAttribute('data-fhs-pattern-id') || undefined,
  };
}

/**
 * Track a component interaction event.
 *
 * @param {string} interactionType - Must be from INTERACTION_TYPES
 * @param {HTMLElement} [element] - Component or descendant; used to get component context
 * @param {Object} [extra] - Optional: filter_type, filter_value, selected_date, current_index, total_items
 */
export function trackComponentInteraction(interactionType, element, extra = {}) {
  if (!VALID_INTERACTION_TYPES.has(interactionType)) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn(
        `[fhs-component-interacted] Invalid interaction_type: "${interactionType}". Must be one of: ${Array.from(VALID_INTERACTION_TYPES).join(', ')}`,
      );
    }
    return;
  }

  const componentEl = element ? findComponentElement(element) : null;
  const componentContext = getComponentContext(componentEl);

  const payload = {
    ...componentContext,
    interaction_type: interactionType,
    ...extra,
  };

  if (typeof window !== 'undefined' && window.fhsAnalytics?.track) {
    window.fhsAnalytics.track(EVENT_NAME, payload);
  } else if (typeof window !== 'undefined') {
    (window.__fhsAnalyticsQueue = window.__fhsAnalyticsQueue || []).push([
      EVENT_NAME,
      payload,
    ]);
  }
}
